import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Card, CardContent, Box, Grid, Typography, Button } from '@mui/material';
import PDFViewer from 'pdf-viewer-reactjs';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import './SwaggerUIFix.css';
import YAML from 'yaml';
import GetAppIcon from '@mui/icons-material/GetApp';
import CodeIcon from '@mui/icons-material/Code';

import DirectoryTree from '../../components/DirectoryTree';
import ExcelPreview from '../../components/ExcelPreview/ExcelPreview';

import { useAuth } from '../../contexts/AuthContext/AuthContext';

function Docs() {
    const [content, setContent] = useState('');
    const [selectedContent, setSelectedContent] = useState('');
    const [selectedKey, setSelectedKey] = useState('');

    const { token } = useAuth();

    const handleSelect = (key, content) => {
        setSelectedKey(key);
        setSelectedContent(content);
    };

    const downloadYAML = () => {
        try {
            const yamlContent = YAML.stringify(YAML.parse(selectedContent));
            const blob = new Blob([yamlContent], { type: 'application/yaml' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${selectedKey.replace(/\\s+/g, "").replace(">", "_to_").trim()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error converting YAML:', error);
        }
    };

    const downloadJSON = () => {
        try {
            const jsonObject = YAML.parse(selectedContent);
            const jsonContent = JSON.stringify(jsonObject, null, 2);
            const blob = new Blob([jsonContent], { type: 'application/json' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${selectedKey.replace(">", "_to_").replace(".yml", "").replace(/\\s+/g, "").trim()}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error converting YAML to JSON:', error);
        }
    };

    const downloadExcel = () => {
        try {
            // Convert base64 back to binary
            const binaryString = atob(selectedContent);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            
            const blob = new Blob([bytes], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = selectedKey.replace(">", "_to_").replace(/\\s+/g, "").trim();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading Excel file:', error);
        }
    };

    const renderFile = () => {
        const fileExtension = selectedKey.split('.').pop().toLowerCase();
        switch (fileExtension) {
            case 'md':
                return <Markdown rehypePlugins={[rehypeRaw]}>{selectedContent}</Markdown>;
            case 'yml':
            case 'yaml':
                const parsedSpec = YAML.parse(selectedContent);
                // Ensure proper OpenAPI version structure
                if (parsedSpec && !parsedSpec.info) {
                    parsedSpec.info = {};
                }
                if (parsedSpec && parsedSpec.info && !parsedSpec.info.version) {
                    parsedSpec.info.version = 'v1.0.0';
                }
                if (parsedSpec && !parsedSpec.openapi && !parsedSpec.swagger) {
                    parsedSpec.openapi = '3.0.0';
                }
                
                return (
                    <div>
                        <div style={{ textAlign: 'right', marginTop: '20px' }}>
                            <Button variant="contained" color="primary" onClick={downloadYAML} startIcon={<GetAppIcon />} style={{ marginRight: '10px' }}>
                                Download YAML
                            </Button>
                            <Button variant="contained" color="secondary" onClick={downloadJSON} startIcon={<CodeIcon />}>
                                Download JSON
                            </Button>
                        </div>
                        <Box sx={{ mt: 2 }}>
                            <SwaggerUI spec={parsedSpec} deepLinking={true}/>
                        </Box>
                    </div>
                );
            case 'pdf':
                return <PDFViewer document={{ base64: selectedContent }} />;
            case 'xlsx':
            case 'xls':
                return (
                    <div>
                        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={downloadExcel} 
                                startIcon={<GetAppIcon />}
                            >
                                Excel herunterladen
                            </Button>
                        </div>
                        <ExcelPreview 
                            base64Content={selectedContent} 
                            filename={selectedKey.split('>').pop().trim()} 
                        />
                    </div>
                );
            default:
                return <Typography>{selectedContent || "Select a file to view its contents."}</Typography>;
        }
    };

    useEffect(() => {
        const fetchSwaggerJson = async () => {
            try {
                const response = await axios.get('/api/v1/docs/', {
                    headers: {
                        Authorization: `JWT ${token}`,
                    },
                });
                setContent(response.data);
            } catch (err) {
                setSelectedContent(err?.response?.data?.message || 'Unable to fetch API documentation');
            }
        };

        fetchSwaggerJson();
    }, []);



    return (
        <Grid container sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'rgb(232, 232, 232)' }}>
            <Grid item xs={3} sx={{ overflow: 'auto' }}>
                <DirectoryTree data={content} onSelect={handleSelect} />
            </Grid>
            <Grid item xs={9} sx={{ overflow: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', m: 4 }}>
                    <Card sx={{ minWidth: '100%' }}>
                        <CardContent>
                            {renderFile()}
                        </CardContent>
                    </Card>
                </Box>
            </Grid>
        </Grid>
    );
}

export default Docs;
